import { FunctionComponent, ReactNode, useEffect, useState } from 'react';

import { InfiniteScrollContainer } from '@/components/common/InfiniteScroll/styles';
import Loader from '@/components/molecules/Loader';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import { Nullable } from '@/types/common';

export interface InfiniteScrollData<T = void> {
  data: Nullable<T>;
  isLoading: boolean;
  hasNextPage?: boolean;
  loader?: ReactNode;
  fetchNextPage: () => void;
}

interface infiniteScrollProps extends Omit<InfiniteScrollData, 'data'>, IntersectionObserverInit {
  children: ReactNode;
}

const InfiniteScroll: FunctionComponent<infiniteScrollProps> = ({
  threshold = 0,
  rootMargin,
  children,
  loader = <Loader />,
  isLoading,
  hasNextPage,
  fetchNextPage,
}) => {
  const [loaderElement, setLoaderElement] = useState<Nullable<HTMLDivElement>>();
  const entry = useIntersectionObserver({
    target: loaderElement,
    threshold,
    rootMargin,
  });

  useEffect(() => {
    if (entry?.isIntersecting && hasNextPage && !isLoading) {
      fetchNextPage();
    }
  }, [entry, hasNextPage, isLoading, fetchNextPage]);

  return (
    <InfiniteScrollContainer>
      <div>{children}</div>
      {hasNextPage && !isLoading && <div ref={(ref) => setLoaderElement(ref)}>{loader}</div>}
    </InfiniteScrollContainer>
  );
};

export default InfiniteScroll;
